/**
 * umService — unified data layer for the University Manager section.
 *
 * When isMockDataUM = true  → delegates to mockService (demo/offline mode).
 * When isMockDataUM = false → delegates to real API services and normalises
 *   the response shape to match what the UM components expect:
 *     paginated: { success, data: { content: [], totalPages, totalElements } }
 *     flat list : { success, data: [] }
 *     single    : { success, data: { ...item } }
 *
 * Endpoints with no real backend equivalent always delegate to mockService.
 */
import { isMockDataUM } from "../../config/featureFlags";
import mockService from "../../mock/mockService";
import campusService from "./campusService";
import locationService from "./locationService";
import facultyService from "./facultyService";
import departmentService from "./departmentService";
import clubService from "./clubService";
import eventService from "./eventService";
import universityService from "./universityService";
import userService from "./userService";
import staffService from "./staffService";
import permissionService from "./permissionService";
import clubScopeService from "./clubScopeService";
import transactionService from "./transactionService";
import dashboardService from "./dashboardService";
import { handleFileDownload } from "../../utils/fileDownloadHelper";

// ── Safe wrapper ─────────────────────────────────────────────────────────────
// Prevents API throws from escaping umService. The interceptor already showed
// the error notification; we just need to return { success: false } so pages
// can clean up their loading state instead of getting stuck forever.
const safeCall = async (fn) => {
  try {
    return await fn();
  } catch {
    return { success: false };
  }
};

// ── Normalisers ──────────────────────────────────────────────────────────────

const normalizePaged = (res) => {
  if (!res?.success) return res || { success: false };
  return {
    success: true,
    data: {
      content: res.data?.data || [],
      totalPages: res.data?.totalPages || 0,
      totalElements: res.data?.totalElements || 0,
    },
  };
};

const normalizeFlat = (res) => {
  if (!res?.success) return res || { success: false };
  return {
    success: true,
    data: res.data?.data || [],
  };
};

const normalizeUser = (u) => ({
  ...u,
  fullName: u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim(),
  active: u.active !== undefined ? u.active : u.isActive,
  banned: u.banned !== undefined ? u.banned : u.isBanned,
});

const normalizeUsersPaged = (res) => {
  const paged = normalizePaged(res);
  if (!paged.success) return paged;
  return { ...paged, data: { ...paged.data, content: paged.data.content.map(normalizeUser) } };
};

const normalizeClubMember = (m) => ({
  ...m,
  memberName: m.memberName || m.userFullName,
  email: m.email || m.userEmail,
});

const normalizeAdmin = (a) => ({
  ...a,
  fullName: a.fullName || `${a.firstName || ""} ${a.lastName || ""}`.trim(),
});

const normalizeAdminsPaged = (res) => {
  const paged = normalizePaged(res);
  if (!paged.success) return paged;
  return { ...paged, data: { ...paged.data, content: paged.data.content.map(normalizeAdmin) } };
};

const normalizeAuditLog = (log) => ({
  ...log,
  actionType: log.actionType || log.action,
  actor: log.actor || log.performedBy,
  targetType: log.targetType || log.entityType,
  targetId: log.targetId || log.entityId,
  createdAt: log.createdAt || log.timestamp,
});

// ── Service ──────────────────────────────────────────────────────────────────

const umService = {
  // ── Campuses ────────────────────────────────────────────────────────────────
  getCampuses: async (params) => {
    if (isMockDataUM) return mockService.getCampuses(params);
    return safeCall(() => campusService.getCampuses(params.page, params.size, params.search, params.status).then(normalizePaged));
  },

  getAllCampuses: async (params) => {
    if (isMockDataUM) return mockService.getAllCampuses(params);
    return safeCall(() => campusService.getCampuses(0, 100, "").then(normalizeFlat));
  },

  createCampus: async (params) => {
    if (isMockDataUM) return mockService.createCampus(params);
    return safeCall(() => campusService.createCampus(params.data));
  },

  updateCampus: async (params) => {
    if (isMockDataUM) return mockService.updateCampus(params);
    return safeCall(() => campusService.updateCampus(params.id, params.data));
  },

  deleteCampus: async (params) => {
    if (isMockDataUM) return mockService.deleteCampus(params);
    return safeCall(() => campusService.deleteCampus(params.id));
  },

  // ── Location ─────────────────────────────────────────────────────────────────
  getCountries: async () => {
    if (isMockDataUM) return { success: true, data: [] };
    return safeCall(async () => {
      const res = await locationService.getCountries();
      if (!res?.success) return res || { success: false };
      return { success: true, data: res.data || [] };
    });
  },

  getCitiesByCountry: async (countryId) => {
    if (isMockDataUM) return { success: true, data: [] };
    return safeCall(async () => {
      const res = await locationService.getCitiesByCountry(countryId);
      if (!res?.success) return res || { success: false };
      return { success: true, data: res.data || [] };
    });
  },

  // ── Faculties ───────────────────────────────────────────────────────────────
  getFaculties: async (params) => {
    if (isMockDataUM) return mockService.getFaculties(params);
    return safeCall(() =>
      facultyService.getFaculties(params.page, params.size, params.search, params.campusId, params.status).then(normalizePaged)
    );
  },

  getAllFaculties: async (params) => {
    if (isMockDataUM) return mockService.getAllFaculties(params);
    return safeCall(() => facultyService.getFaculties(0, 100, "", null, true).then(normalizeFlat));
  },

  createFaculty: async (params) => {
    if (isMockDataUM) return mockService.createFaculty(params);
    return safeCall(() => facultyService.createFaculty(params.data));
  },

  updateFaculty: async (params) => {
    if (isMockDataUM) return mockService.updateFaculty(params);
    return safeCall(() => facultyService.updateFaculty(params.id, params.data));
  },

  deleteFaculty: async (params) => {
    if (isMockDataUM) return mockService.deleteFaculty(params);
    return safeCall(() => facultyService.deleteFaculty(params.id));
  },

  // ── Departments ─────────────────────────────────────────────────────────────
  getDepartments: async (params) => {
    if (isMockDataUM) return mockService.getDepartments(params);
    return safeCall(() =>
      departmentService.getDepartments(params.page, params.size, params.search, params.facultyId, params.status).then(normalizePaged)
    );
  },

  createDepartment: async (params) => {
    if (isMockDataUM) return mockService.createDepartment(params);
    return safeCall(() => departmentService.createDepartment(params.data));
  },

  updateDepartment: async (params) => {
    if (isMockDataUM) return mockService.updateDepartment(params);
    return safeCall(() => departmentService.updateDepartment(params.id, params.data));
  },

  deleteDepartment: async (params) => {
    if (isMockDataUM) return mockService.deleteDepartment(params);
    return safeCall(() => departmentService.deleteDepartment(params.id));
  },

  // ── Clubs ───────────────────────────────────────────────────────────────────
  getClubs: async (params) => {
    if (isMockDataUM) return mockService.getClubs(params);
    return safeCall(() =>
      clubService.getClubs(params.page, params.size, params.search, params.campusId, params.status).then(normalizePaged)
    );
  },

  getAllClubs: async (params) => {
    if (isMockDataUM) return mockService.getAllClubs(params);
    return safeCall(() => clubService.getClubs(0, 100, "").then(normalizeFlat));
  },

  getClubById: async (id, params) => {
    if (isMockDataUM) return mockService.getClubById(id, params);
    return safeCall(() => clubService.getClub(id));
  },

  createClub: async (params) => {
    if (isMockDataUM) return mockService.createClub(params);
    return safeCall(() => clubService.createClub(params.data));
  },

  updateClub: async (params) => {
    if (isMockDataUM) return mockService.updateClub(params);
    return safeCall(() => clubService.updateClub(params.id, params.data));
  },

  deleteClub: async (params) => {
    if (isMockDataUM) return mockService.deleteClub(params);
    return safeCall(() => clubService.deleteClub(params.id));
  },

  // ── Club Members ────────────────────────────────────────────────────────────
  getClubMembers: async (params) => {
    if (isMockDataUM) return mockService.getClubMembers(params);
    return safeCall(async () => {
      const paged = normalizePaged(await clubService.getClubMembers(params.clubId, params.page, params.size));
      if (!paged.success) return paged;
      return { ...paged, data: { ...paged.data, content: paged.data.content.map(normalizeClubMember) } };
    });
  },

  addClubMember: async (params, tenantCtx) => {
    if (isMockDataUM) return mockService.addClubMember(params, tenantCtx);
    return safeCall(() => clubService.assignClubRole(params.clubId, params.userId, params.role));
  },

  updateClubMemberRole: async (params, tenantCtx) => {
    if (isMockDataUM) return mockService.updateClubMemberRole(params, tenantCtx);
    return safeCall(() => clubService.assignClubRole(params.clubId, params.userId, params.role));
  },

  removeClubMember: async (params, tenantCtx) => {
    if (isMockDataUM) return mockService.removeClubMember(params, tenantCtx);
    return safeCall(() => clubService.revokeClubMember(params.clubId, params.userId));
  },

  getClubMembersDirectory: async (params) => {
    return safeCall(async () => {
      const paged = normalizePaged(await clubService.getClubMembersDirectory(params.clubId, params.page, params.size));
      if (!paged.success) return paged;
      return { ...paged, data: { ...paged.data, content: paged.data.content.map(normalizeClubMember) } };
    });
  },

  exportClubMembersDirectory: async (clubId, format) => {
    try {
      const res = await clubService.exportClubMembersDirectory(clubId, format);
      return { success: handleFileDownload(res, `club-members-${clubId}.${format.toLowerCase()}`) };
    } catch { return { success: false }; }
  },

  getClubStatistics: async (clubId) => {
    return safeCall(() => clubService.getClubStatistics(clubId));
  },

  exportClubStatistics: async (clubId) => {
    try {
      const res = await clubService.exportClubStatistics(clubId);
      return { success: handleFileDownload(res, `club-statistics-${clubId}.pdf`) };
    } catch { return { success: false }; }
  },

  exportAllClubsReport: async (startDate, endDate) => {
    try {
      const res = await clubService.exportAllClubsReport(startDate, endDate);
      return { success: handleFileDownload(res, `all-clubs-report.pdf`) };
    } catch { return { success: false }; }
  },

  // ── Events ──────────────────────────────────────────────────────────────────
  getEvents: async (params) => {
    if (isMockDataUM) return mockService.getEvents(params);
    return safeCall(() =>
      eventService.getEvents({
        page: params.page,
        size: params.size,
        campusId: params.campusId,
        clubId: params.clubId,
        eventStatus: params.eventStatus,
        status: params.status,
      }).then(normalizePaged)
    );
  },

  getEventById: async (id, params) => {
    if (isMockDataUM) return mockService.getEventById(id, params);
    return safeCall(() => eventService.getEvent(id));
  },

  createEvent: async (params) => {
    if (isMockDataUM) return mockService.createEvent(params);
    return safeCall(() => eventService.createEvent(params.data));
  },

  updateEvent: async (params) => {
    if (isMockDataUM) return mockService.updateEvent(params);
    return safeCall(() => eventService.updateEvent(params.id, params.data));
  },

  approveEvent: async (id) => {
    if (isMockDataUM) return mockService.approveEvent(id);
    return safeCall(() => eventService.approveEvent(id));
  },

  rejectEvent: async (id, rejectionMessage) => {
    if (isMockDataUM) return mockService.rejectEvent(id, rejectionMessage);
    return safeCall(() => eventService.rejectEvent(id, rejectionMessage));
  },

  cancelEvent: async (id) => {
    if (isMockDataUM) return mockService.cancelEvent(id);
    return safeCall(() => eventService.cancelEvent(id));
  },

  deleteEvent: async (id) => {
    if (isMockDataUM) return mockService.deleteEvent(id);
    return safeCall(() => eventService.deleteEvent(id));
  },

  getTicketAttendants: async (id) => {
    if (isMockDataUM) return mockService.getTicketAttendants(id);
    return safeCall(() => eventService.getTicketAttendants(id).then(normalizeFlat));
  },

  addTicketAttendants: async (id, emails) => {
    if (isMockDataUM) return mockService.addTicketAttendants(id, emails);
    return safeCall(() => eventService.addTicketAttendants(id, emails));
  },

  removeTicketAttendant: async (id, email) => {
    if (isMockDataUM) return mockService.removeTicketAttendant(id, email);
    return safeCall(() => eventService.removeTicketAttendant(id, email));
  },

  getEventStatistics: async (id) => {
    return safeCall(() => eventService.getEventStatistics(id));
  },

  exportEventStatistics: async (id) => {
    try {
      const res = await eventService.exportEventStatistics(id);
      return { success: handleFileDownload(res, `event-statistics-${id}.pdf`) };
    } catch { return { success: false }; }
  },

  getEventParticipants: async (params) => {
    return safeCall(() => eventService.getEventParticipants(params.eventId, params.page, params.size).then(normalizePaged));
  },

  exportEventParticipants: async (id, format) => {
    try {
      const res = await eventService.exportEventParticipants(id, format);
      return { success: handleFileDownload(res, `event-participants-${id}.${format.toLowerCase()}`) };
    } catch { return { success: false }; }
  },

  getEventTicketOwners: async (params) => {
    return safeCall(() => eventService.getEventTicketOwners(params.eventId, params.page, params.size).then(normalizePaged));
  },

  exportEventTicketOwners: async (id, format) => {
    try {
      const res = await eventService.exportEventTicketOwners(id, format);
      return { success: handleFileDownload(res, `event-ticket-owners-${id}.${format.toLowerCase()}`) };
    } catch { return { success: false }; }
  },

  // ── Club Events (feature component uses this via deleteClubEvent) ────────────
  deleteClubEvent: async (params, tenantCtx) => {
    if (isMockDataUM) return mockService.deleteClubEvent(params, tenantCtx);
    return safeCall(() => eventService.deleteEvent(params.eventId));
  },

  // ── Users ───────────────────────────────────────────────────────────────────
  getUsersDemo: async (params) => {
    if (isMockDataUM) return mockService.getUsersDemo(params);
    return safeCall(() =>
      userService.getUsers(params.page, params.size, params.filters?.facultyId).then(normalizeUsersPaged)
    );
  },

  getUserDemoDetails: async (id, params) => {
    if (isMockDataUM) return mockService.getUserDemoDetails(id, params);
    return safeCall(async () => {
      const res = await userService.getUser(id);
      if (!res?.success) return res;
      return { success: true, data: normalizeUser(res.data) };
    });
  },

  // Users: search for available users (used in club member assignment)
  getPermissionUsers: async (params) => {
    if (isMockDataUM) return mockService.getPermissionUsers(params);
    return safeCall(() => userService.getUsers(params.page || 0, params.size || 100).then(normalizeUsersPaged));
  },

  // No create/update/delete user endpoint in university-admin API
  createUserDemo: (params) => mockService.createUserDemo(params),
  updateUserDemo: (params) => mockService.updateUserDemo(params),
  deleteUserDemo: (params) => mockService.deleteUserDemo(params),

  changeUserDemoStatusAndRole: async (params) => {
    if (isMockDataUM) return mockService.changeUserDemoStatusAndRole(params);
    const { id, data } = params;
    return safeCall(async () => {
      const res = await userService.changeUserStatusAndRole(id, data);
      if (!res?.success) return res || { success: false };
      return { success: true, data: normalizeUser(res.data) };
    });
  },

  // ── University ──────────────────────────────────────────────────────────────
  getUniversity: async (id) => {
    if (isMockDataUM) return mockService.getUniversity(id);
    return safeCall(() => universityService.getMyUniversity());
  },

  updateUniversity: async (id, data) => {
    if (isMockDataUM) return mockService.updateUniversity(id, data);
    return safeCall(() =>
      universityService.updateLoginPolicy({
        loginEnabled: data.loginEnabled,
        registrationEnabled: data.registrationEnabled,
        maintenanceMode: data.maintenanceMode,
      })
    );
  },

  updateUniversityLogo: async (photoId) => {
    return safeCall(() => universityService.updateLogo(photoId));
  },

  // ── Audit Logs ──────────────────────────────────────────────────────────────
  getActivityLogs: async (params) => {
    if (isMockDataUM) return mockService.getActivityLogs(params);
    return safeCall(async () => {
      const paged = normalizePaged(
        await universityService.getAuditLogs(params.dateFrom, params.dateTo, params.page, params.size)
      );
      if (!paged.success) return paged;
      return { ...paged, data: { ...paged.data, content: paged.data.content.map(normalizeAuditLog) } };
    });
  },

  // ── Staff / Admin Management (Yetki Yönetimi — Super Admin only) ────────────
  getStaffAdmins: async (page = 0, size = 10, config = {}) => {
    return safeCall(() => staffService.getAdmins(page, size, config).then(normalizeAdminsPaged));
  },

  getAdminDetail: async (userId) => {
    return safeCall(async () => {
      const res = await staffService.getAdmin(userId);
      if (!res?.success) return res;
      return { success: true, data: normalizeAdmin(res.data) };
    });
  },

  createStaffAdmin: async (data) => {
    return safeCall(async () => {
      const res = await staffService.createAdmin(data);
      if (!res?.success) return res;
      return { success: true, data: normalizeAdmin(res.data) };
    });
  },

  promoteAdmin: async (email) => {
    return safeCall(() => staffService.promoteAdmin(email));
  },

  changeAdminType: async (userId, adminType) => {
    return safeCall(() => staffService.changeAdminType(userId, adminType));
  },

  setAllClubsAccess: async (userId, enabled) => {
    return safeCall(() => staffService.setAllClubsAccess(userId, enabled));
  },

  // ── Endpoint Permissions (RBAC) ──────────────────────────────────────────────
  getPermissionsCatalog: async () => {
    return safeCall(async () => {
      const res = await permissionService.getCatalog();
      if (!res?.success) return res || { success: false };
      return { success: true, data: res.data || [] };
    });
  },

  getAdminGrants: async (adminUserId) => {
    return safeCall(async () => {
      const res = await permissionService.getGrants(adminUserId);
      if (!res?.success) return res || { success: false };
      return { success: true, data: res.data || [] };
    });
  },

  grantPermission: async (params) => {
    return safeCall(() => permissionService.grant(params));
  },

  revokePermission: async (params) => {
    return safeCall(() => permissionService.revoke(params));
  },

  // ── Club Scopes (ABAC) ───────────────────────────────────────────────────────
  getAdminClubScopes: async (adminUserId) => {
    return safeCall(async () => {
      const res = await clubScopeService.getScopes(adminUserId);
      if (!res?.success) return res || { success: false };
      return { success: true, data: res.data || [] };
    });
  },

  grantClubScope: async (params) => {
    return safeCall(() => clubScopeService.grantScope(params));
  },

  revokeClubScope: async (params) => {
    return safeCall(() => clubScopeService.revokeScope(params));
  },

  getDashboardStats: (params) => {
    if (isMockDataUM) return mockService.getDashboardStats(params);
    return safeCall(() => dashboardService.getStats());
  },

  // ── No real API — always mock ───────────────────────────────────────────────
  getTransfers: (params) => mockService.getTransfers(params),

  // ── Transactions ────────────────────────────────────────────────────────────
  getTransactions: async (params) => {
    if (isMockDataUM) return mockService.getTransactions(params);
    return safeCall(() =>
      transactionService.getTransactions({
        page: params.page,
        size: params.size,
        userEmail: params.userEmail,
        startDate: params.startDate,
        endDate: params.endDate,
        status: params.status,
        type: params.type,
        eventId: params.eventId,
        clubId: params.clubId,
      }).then(normalizePaged)
    );
  },

  getTransactionById: async (id) => {
    if (isMockDataUM) return mockService.getTransactionById(id);
    return safeCall(() => transactionService.getTransactionById(id));
  },
};

export default umService;
